import queryKeys from '@/constants/queryKeys';
import { organizationMembersForAdminGet } from '@/services/api/requests/organization';
import { TUser, TUserAffiliation } from '@/types/User.types';
import { Box, Center, Flex, HStack, Image, Select, Text, Tooltip, VStack } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { Table, Header, HeaderRow, Body, Row, HeaderCell, Cell } from '@table-library/react-table-library/table';
import { useTheme } from '@table-library/react-table-library/theme';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppStore } from '@/store/useAppStore';
import CustomButton from '@/components/atoms/CustomButton';
import { FORM_FIELD_WIDTH, MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
import OrganizationRosterDeleteMemberModal from './OrganizationRosterDeleteMemberModal';
import { TRosterItemWithIdAndRole } from '@/types/Roster.types';
import iconsPng from '@/assets/img/png/icons';
import tableTheme from '@/theme/components/table';
import { TABLE_COLUMNS_ORG_ROSTER_VIEW } from '@/utils/tableConfigs';
import roles from '@/constants/roles';
import useRemoveMemberFromOrganization from '@/hooks/organization/removeMemberFromOrganization';
import OrganizationRosterAddMemberModal from './OrganizationRosterAddMemberModal';
import OrgMemberPendingInvite from './OrgMemberPendingReinvite';
import CompactTablePagination from '@/components/molecules/CompactTablePagination/CompactTablePagination';
import OrganizationRosterPeerSupportModal from './OrganizationRosterPeerSupportModal';
import OrganizationRosterDowngradePeerSupportModal from './OrganizationRosterDowngradePeerSupportModal';
import useDowngradePeerSupportToMember from '@/hooks/peersupport/downgradePeerSupportToMember';
import ConfirmationModal from '@/components/molecules/ConfirmationModal';
import useReinviteMemberToOrganization from '@/hooks/organization/reinviteMemberToOrganization';
import { usePaginatedSearch } from '@/hooks/usePaginatedSearch';
import SearchInputBox from '@/components/atoms/SearchInputBox';
import { MIN_CHARS_TO_DO_SEARCH, USER_AFFILIATION_OPTIONS } from '@/constants';
import { capitalize } from '@/utils/stringUtils';
import PeerSupportSwitch from './PeerSupportSwitch';
import svgIcons from '@/assets/svg/icons';
import colors from '@/theme/colors';
import OrganizationRosterUpdateAffiliationModal from './OrganizationRosterUpdateAffiliationModal';
import useUpdateUserAffiliation from '@/hooks/organization/updateUserAffiliation';
import LogoSpinner from '@/components/atoms/LogoSpinner';
import PeerSupportLeadIconTrigger from './PeerSupportLeadIconTrigger';
import OrgMembersRosterBulkAdd from './OrgMembersRosterBulkAdd';

const WAIT_LOADING_MILLISECONDS = 700;

const TOTAL_COLUMNS_WITHOUT_DELETE = TABLE_COLUMNS_ORG_ROSTER_VIEW.length;

const COLUMN_PERCENTAGES = '12% 12% 12% 25% 10% 12% 9% 8%';

const OrgMembersRoster = () => {
  const theme = useTheme({
    ...tableTheme,
    Table: `${tableTheme.Table}; --data-table-library_grid-template-columns: ${COLUMN_PERCENTAGES}`,
  });

  const queryClient = useQueryClient();

  const { organization } = useAppStore();

  const [isBulkAdding, setIsBulkAdding] = useState(false);

  const [addMemberModalVisible, setAddMemberModalVisible] = useState(false);
  const [isReinviteModalOpened, setIsReinviteModalOpened] = useState(false);
  const [deleteConfirmationModalVisible, setDeleteConfirmationModalVisible] = useState(false);
  const [downgradeConfirmationModalVisible, setDowngradeConfirmationModalVisible] = useState(false);
  const [updateAffiliationConfirmationModalVisible, setUpdateAffiliationConfirmationModalVisible] = useState(false);

  const [itemToDeleteEmail, setItemToDeleteEmail] = useState('');
  const [itemToDeleteId, setItemToDeleteId] = useState('');

  const onClickDelete = useCallback((item: TRosterItemWithIdAndRole) => {
    setItemToDeleteId(item.id);
    setItemToDeleteEmail(item.Email);
    setDeleteConfirmationModalVisible(true);
  }, []);

  const [peerSupportModalVisible, setPeerSupportModalVisible] = useState(false);
  const [itemToUpgradeToPeerSupportId, setItemToUpgradeToPeerSupportId] = useState('');
  const [itemToUpgradeToPeerSupportName, setItemToUpgradeToPeerSupportName] = useState('second');

  const [itemToDowngradeFromPeerSupportName, setItemToDowngradeFromPeerSupportName] = useState('');
  const [itemToDowngradeFromPeerSupportId, setItemToDowngradeFromPeerSupportId] = useState('');

  const onClickPeerSupport = useCallback((item: TRosterItemWithIdAndRole) => {
    if (item.isPeerSupport) {
      setItemToDowngradeFromPeerSupportId(item.id);
      setItemToDowngradeFromPeerSupportName(`${item['First Name']} ${item['Last Name']}`);
      setDowngradeConfirmationModalVisible(true);
    } else {
      setItemToUpgradeToPeerSupportId(item.id);
      setItemToUpgradeToPeerSupportName(`${item['First Name']} ${item['Last Name']}`);
      setPeerSupportModalVisible(true);
    }
  }, []);

  const [itemToUpdateAffiliation, setItemToUpdateAffiliation] = useState<TRosterItemWithIdAndRole>();
  const [newAffiliation, setNewAffiliation] = useState<TUserAffiliation>();

  const onClickUpdateAffiliation = useCallback((item: TRosterItemWithIdAndRole, newAffiliation: TUserAffiliation) => {
    setItemToUpdateAffiliation(item);
    setNewAffiliation(newAffiliation);
    setUpdateAffiliationConfirmationModalVisible(true);
  }, []);

  const tableColumns = useMemo(() => {
    const columns: {
      label: string | JSX.Element;
      renderCell: (item: TRosterItemWithIdAndRole) => string | JSX.Element;
    }[] = TABLE_COLUMNS_ORG_ROSTER_VIEW;

    if (columns.length === TOTAL_COLUMNS_WITHOUT_DELETE) {
      columns.push({
        label: <Flex maxWidth={'100px'}></Flex>,
        renderCell: () => '',
      });
    }
    return columns;
  }, [onClickDelete]);

  const [filterText, setFilterText] = useState('');

  const {
    isLoading: isOrgMembersLoading,
    data: orgMembers,
    refetch: refetchOrgMembers,
    isRefetching: isFetchingOrgMembers,
    pagination,
  } = usePaginatedSearch<TUser[], string, string>({
    query: organizationMembersForAdminGet,
    queryKey: queryKeys.organization.members,
    searchFilterType: '',
    searchFilterText: filterText.length >= MIN_CHARS_TO_DO_SEARCH ? filterText : undefined,
  });

  const [isWaitLoading, setIsWaitLoading] = useState(false); // improves UX by having the UI start the loader immediately after an action (add/delete member); this is needed because we have to wait 2 seconds to fetch data from auth0

  const isLoading = isOrgMembersLoading || isWaitLoading;

  const [userNameToReinvite, setUserNameToReinvite] = useState('');
  const [userIdToReinvite, setUserIdToReinvite] = useState('');

  const { mutate: reinviteMemberToOrganization, isPending: isMemberReinvitePending } =
    useReinviteMemberToOrganization();

  const mapBeUserToTableUser = useCallback(
    (users: TUser[]): TRosterItemWithIdAndRole[] =>
      users.map((user) => ({
        'First Name': user.firstName ?? user.user_metadata.firstName,
        'Last Name': user.lastName ?? user.user_metadata.lastName,
        Phone: user.user_metadata.phone ?? '/',
        Email: user.email,
        Affiliation: (user.userAffiliation?.toLowerCase() as TUserAffiliation) ?? 'None',
        'Peer Support': user.roles?.includes(roles.peerSupport),
        Invitation:
          user.roles?.includes(roles.orgAdmin) || !!user.user_metadata.sex ? (
            'Accepted'
          ) : (
            <OrgMemberPendingInvite
              isPending={isMemberReinvitePending}
              onClick={() => {
                setUserNameToReinvite(
                  `${user?.firstName ?? user?.user_metadata?.firstName} ${user?.lastName ?? user?.user_metadata.lastName}`,
                );
                setUserIdToReinvite(user.user_id);
                setIsReinviteModalOpened(true);
              }}
            />
          ),
        id: user.user_id,
        isAdmin: user.roles?.includes(roles.orgAdmin),
        isPeerSupport: user.roles?.includes(roles.peerSupport),
        'Peer Support Lead': user.roles?.includes(roles.peerSupportLead),
      })),
    [isMemberReinvitePending],
  );

  const [rawData, setRawData] = useState([] as TRosterItemWithIdAndRole[]);

  useEffect(() => {
    if (orgMembers) {
      setRawData(mapBeUserToTableUser(orgMembers));
    }
  }, [orgMembers, mapBeUserToTableUser]);

  const data = useMemo(() => ({ nodes: rawData ?? [] }), [rawData]);

  const reFetch = useCallback(() => {
    // TODO: see how to improve this... remove the wishful 2s wait and waitLoading...
    setTimeout(() => setIsWaitLoading(true), WAIT_LOADING_MILLISECONDS);
    setTimeout(() => {
      pagination.getFirstPage();
      queryClient.invalidateQueries({ queryKey: [queryKeys.organization.members], exact: false });
      setIsWaitLoading(false);
    }, 2000); // if we don't wait 2 seconds before re-fetching, newly added user doesn't appear
  }, [refetchOrgMembers]);

  const { mutate: removeOrgMember } = useRemoveMemberFromOrganization(reFetch);

  const deleteMember = useCallback(
    (memberId: string) => {
      setTimeout(() => setIsWaitLoading(true), WAIT_LOADING_MILLISECONDS);
      removeOrgMember(memberId);
    },
    [removeOrgMember],
  );

  const { mutate: downgradePeerSupportToMemberMutate, isPending: isDowngradePending } = useDowngradePeerSupportToMember(
    () => setDowngradeConfirmationModalVisible(false),
  );

  const downgradePeerSupportToMember = useCallback(() => {
    downgradePeerSupportToMemberMutate({
      organizationId: organization?._id ?? '',
      userId: itemToDowngradeFromPeerSupportId,
    });
  }, [organization, itemToDowngradeFromPeerSupportId]);

  const { mutate: updateUserAffiliation, isPending: isUpdatePending } = useUpdateUserAffiliation(
    organization?._id ?? '',
    () => {
      setUpdateAffiliationConfirmationModalVisible(false);
      reFetch();
    },
  );

  const onClickUpdateUserAffiliation = useCallback(() => {
    setTimeout(() => setIsWaitLoading(true), WAIT_LOADING_MILLISECONDS);
    updateUserAffiliation({ userId: itemToUpdateAffiliation?.id ?? '', newAffiliation: newAffiliation! });
  }, [itemToUpdateAffiliation, newAffiliation]);

  const [rowHoveredId, setRowHoveredId] = useState('');

  if (isBulkAdding) {
    return (
      <OrgMembersRosterBulkAdd
        onMemberAddFinish={() => {
          setIsBulkAdding(false);
          reFetch();
        }}
        onGoBack={() => setIsBulkAdding(false)}
      />
    );
  }

  return (
    <Center marginTop={'30px'} paddingBottom={'10vh'}>
      <VStack width={MAX_CONTAINER_WIDTH} gap={0}>
        <HStack width={'100%'} justifyContent={'space-between'} alignItems={'center'}>
          <Text variant={'loraTitle'}>{organization?.name}&apos;s Roster</Text>

          <HStack gap={'30px'}>
            <SearchInputBox setFilterText={setFilterText} isSearching={isFetchingOrgMembers} />

            <CustomButton
              label="Bulk Add via CSV"
              isTransparent
              labelColor="text.mediumBlue"
              labelHoverColor="text.darkBlue"
              onClick={() => setIsBulkAdding(true)}
            />

            <CustomButton
              width={'150px'}
              height={'55px'}
              onClick={() => setAddMemberModalVisible(true)}
              backgroundColor="primary.500"
              labelColor="extra.white"
              label="Add Member"
            />
          </HStack>
        </HStack>

        {isLoading ? (
          <Center height={'860px'} paddingBottom={'20vh'}>
            <LogoSpinner />
          </Center>
        ) : (
          <VStack width={'100%'}>
            <Box marginTop={'60px'} width={'100%'}>
              {data.nodes.length > 0 ? (
                <>
                  <Table data={data} theme={theme} layout={{ custom: true }}>
                    {(tableDataItems: TRosterItemWithIdAndRole[]) => (
                      <>
                        <Header>
                          <HeaderRow>
                            {tableColumns.map((column) => (
                              <HeaderCell key={column.label.toString()}>{column.label}</HeaderCell>
                            ))}
                          </HeaderRow>
                        </Header>

                        <Body>
                          {tableDataItems.map((item, index) => {
                            return (
                              <Row key={item.id} item={item}>
                                {/* Box added with display={'contents'} to allow for mouseOver event tracking */}
                                <Box
                                  className={`tr tr-body ${index < tableDataItems.length - 1 ? 'border-bottom-items-container' : ''}`}
                                  display={'contents'}
                                  onMouseEnter={() => setRowHoveredId(item.id)}
                                  onMouseLeave={() => setRowHoveredId('')}
                                  paddingBottom={10}
                                  marginBottom={10}
                                >
                                  <Cell>{item['First Name']}</Cell>
                                  <Cell>{item['Last Name']}</Cell>
                                  <Cell>{item.Phone}</Cell>
                                  <Cell>{item.Email}</Cell>
                                  <Cell>
                                    {item.isAdmin ? (
                                      <Text variant={'urbanistSemiBold'} textAlign={'start'}>Admin</Text>
                                    ) : (
                                      <Select
                                        focusBorderColor="transparent"
                                        className="custom-input custom-select-padding"
                                        value={item.Affiliation}
                                        style={{
                                          fontSize: '16px',
                                          width: 150,
                                          marginLeft: '-15px',
                                          color: colors.text.mediumBlue,
                                          border: 0,
                                          cursor: 'pointer',
                                        }}
                                        _hover={{ opacity: 0.7 }}
                                        onChange={(event) =>
                                          onClickUpdateAffiliation(item, event.target.value as TUserAffiliation)
                                        }
                                      >
                                        {USER_AFFILIATION_OPTIONS.map((affiliationOption) => (
                                          <option key={affiliationOption} value={affiliationOption}>
                                            {capitalize(affiliationOption)}
                                          </option>
                                        ))}
                                      </Select>
                                    )}
                                  </Cell>
                                  <Cell>{item.Invitation}</Cell>
                                  <Cell>
                                    <HStack gap={'10px'}>
                                      <PeerSupportSwitch
                                        checked={item['Peer Support']}
                                        onChange={() => onClickPeerSupport(item)}
                                      />

                                      <PeerSupportLeadIconTrigger
                                        isPeerSupportLead={!!item['Peer Support Lead']}
                                        isPeerSupport={item['Peer Support']}
                                        isOrgAdmin={item.isAdmin}
                                        userId={item.id}
                                        isRowHovered={rowHoveredId === item.id}
                                        userFullName={`${item['First Name']} ${item['Last Name']}`}
                                      />
                                    </HStack>
                                  </Cell>
                                  {/* Add in "Admin" text for admins or dropdown button for regular users  */}
                                  <Cell>
                                    <Tooltip
                                      label={`Admins can not be deleted`}
                                      placement={'right-start'}
                                      color={'text.mediumBlue'}
                                      background={'white'}
                                      maxWidth={FORM_FIELD_WIDTH}
                                      borderRadius={'8px'}
                                      fontWeight={600}
                                      padding={'10px'}
                                      display={item.isAdmin ? 'flex' : 'none'}
                                      zIndex={20}
                                    >
                                      <VStack paddingY={'10px'}>
                                        <CustomButton
                                          hoverOpacityDrop
                                          onClick={() => onClickDelete(item)}
                                          iconBeforeLabelSvgSize={16}
                                          iconBeforeLabelSvgBoxHeight={14}
                                          iconBeforeLabelSvgBoxWidth={12}
                                          iconBeforeLabelSvgPath={svgIcons.trashCan}
                                          iconBeforeLabelSvgColor={colors.extra.red}
                                          // hoverBackgroundColor={colors.extra.lightBlue}
                                          width={'30px'}
                                          height={'30px'}
                                          disabledColor="#fff"
                                          labelStyle={{ marginLeft: 0, marginTop: '6px' }}
                                          style={{ minWidth: '30px', borderRadius: '50%', paddingInline: 0 }}
                                          disabled={item.isAdmin}
                                        />
                                      </VStack>
                                    </Tooltip>
                                  </Cell>
                                </Box>
                              </Row>
                            );
                          })}
                        </Body>
                      </>
                    )}
                  </Table>
                  <CompactTablePagination pagination={pagination} isFetchingNextPage={isFetchingOrgMembers} />
                </>
              ) : (
                <VStack
                  paddingY={'60px'}
                  justifyContent={'center'}
                  backgroundColor={'extra.white'}
                  borderRadius={'10px'}
                  width={MAX_CONTAINER_WIDTH}
                >
                  <Image alt="notepad" src={iconsPng.notepad} width={120} height={120} />
                  <Text variant={'urbanistSemiBoldSmallTitle'} color={'text.mediumGray'} marginTop={'30px'}>
                    {filterText.length >= MIN_CHARS_TO_DO_SEARCH
                      ? 'No members found.'
                      : 'Start by inviting/adding members to roster.'}
                  </Text>
                </VStack>
              )}
            </Box>
          </VStack>
        )}

        {/* Add Member modal */}
        <OrganizationRosterAddMemberModal
          addMemberModalVisible={addMemberModalVisible}
          setAddMemberModalVisible={setAddMemberModalVisible}
          reFetch={reFetch}
        />

        {/* Reinvite Member modal */}
        <ConfirmationModal
          title="Confirm Reinvite"
          description={`Send another invite to ${userNameToReinvite}?`}
          confirmButtonText="Reinvite"
          isOpen={isReinviteModalOpened}
          onClose={() => setIsReinviteModalOpened(false)}
          onConfirm={() => {
            reinviteMemberToOrganization(userIdToReinvite);
            setIsReinviteModalOpened(false);
          }}
        />

        <OrganizationRosterDeleteMemberModal
          deleteConfirmationModalVisible={deleteConfirmationModalVisible}
          setDeleteConfirmationModalVisible={setDeleteConfirmationModalVisible}
          deleteItem={deleteMember}
          itemToDeleteId={itemToDeleteId}
          itemToDeleteEmail={itemToDeleteEmail}
        />

        <OrganizationRosterPeerSupportModal
          peerSupportModalVisible={peerSupportModalVisible}
          setPeerSupportModalVisible={setPeerSupportModalVisible}
          organizationId={organization?._id ?? ''}
          userToUpgradeToPeerSupportId={itemToUpgradeToPeerSupportId}
          itemToUpgradeToPeerSupportName={itemToUpgradeToPeerSupportName}
        />

        <OrganizationRosterDowngradePeerSupportModal
          downgradeConfirmationModalVisible={downgradeConfirmationModalVisible}
          setDowngradeConfirmationModalVisible={setDowngradeConfirmationModalVisible}
          downgradeItem={downgradePeerSupportToMember}
          itemToDowngradeName={itemToDowngradeFromPeerSupportName}
          itemToDowngradeId={itemToDowngradeFromPeerSupportId}
          isDowngradePending={isDowngradePending}
        />

        <OrganizationRosterUpdateAffiliationModal
          confirmationModalVisible={updateAffiliationConfirmationModalVisible}
          setConfirmationModalVisible={setUpdateAffiliationConfirmationModalVisible}
          updateItemAffiliation={onClickUpdateUserAffiliation}
          itemToUpdate={itemToUpdateAffiliation}
          newAffiliation={newAffiliation}
          isUpdatePending={isUpdatePending}
        />
      </VStack>
    </Center>
  );
};

export default OrgMembersRoster;
